import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { FileChangedEvent, FileUploaderItem, IFileUploader, SabisComboBox2Component, SabisInput2Component, SabisNgxCheckboxComponent, SabisNgxDatepickerComponent, SabisNgxFilePickerComponent } from 'sabis-components2';
import { EmailFilterDTO, EmailsService } from './emails.service';
import { CareersUploader } from './../../core/file-uploaders/careeers-fileuploader.service';
import { EmailEditorDTO, EmailEditorInitDTO, EmailTemplateDTO } from './models/api-models';
import { finalize, Subscription } from 'rxjs';
import { CardViewComponent } from '../card-view/card-view.component';
import { SabisTextEditor2Component } from 'sabis-editor2';

/** TODO: rename to sca-email-editor */
@Component({
  selector: 'sca-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  imports: [
    FormsModule,
    CardViewComponent,
    SabisInput2Component,
    SabisComboBox2Component,
    SabisNgxCheckboxComponent,
    SabisNgxFilePickerComponent,
    SabisNgxDatepickerComponent,
    SabisTextEditor2Component,
  ]
})
export class EmailComponent implements OnInit, OnDestroy, OnChanges {
  @Input('noTemplate') noTemplate: boolean = false;
  @Input('email') email!: EmailEditorDTO;
  @Input('isDisabled') isDisabled: boolean = false;
  @Input('filterEmailTemplates') filterEmailTemplates: boolean = false;
  @Input('allowedEmailTemplateIds') allowedEmailTemplateIds: number[] = null!;
  @Input('canAttachDocuments') canAttachDocuments: boolean = false;
  @Input('showRecipient') showRecipient: boolean = true;
  @Output('isLoading') isLoadingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('f') formViewChild!: NgForm;
  @Input('showEmailCC') showEmailCC: boolean = false;
  @Input('scheduleMinDate') scheduleMinDate: Date | null = null;
  @Input('scheduleMaxDate') scheduleMaxDate: Date | null = null;
  @Input('showScheduleForLater') showScheduleForLater: boolean = true;

  public emailTemplates: EmailTemplateDTO[] = [];
  //Priorities
  //public selectedPriorityId: number | null = null;
  //public priorities: LookupItem<any>[] = [];

  public isScheduledForLater: boolean = false;

  public isLoading: boolean = true;
  public isDisabledInternal: boolean = true;

  private _defaultEmail: string = "";
  private _currentUserEmail: string = "";

  public uploader: IFileUploader = this._uploader;
  public attachmentFileDirectoryPath: string = 'email-attachments';//N.B: if changed -> report scheduler in backend should be updated also
  public attachedFiles: FileUploaderItem<any>[] = [];

  private _getEmailEditorDataSubscription: Subscription = new Subscription();
  public minDate = new Date();
  public maxDate: Date | undefined;

  constructor(private _emailsService: EmailsService, private _uploader: CareersUploader) { }

  private setScheduleMaxDate(): void {
    if (this.scheduleMaxDate) {
      this.maxDate = new Date(this.scheduleMaxDate!);
      this.maxDate.setDate(this.maxDate.getDate() - 1);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scheduleMaxDate'] != null) {
      this.setScheduleMaxDate();
    }
  }

  ngOnInit(): void {
    this.loadEmailLookups();
    // this.setScheduleMaxDate();
  }

  ngOnDestroy(): void {
    this._getEmailEditorDataSubscription.unsubscribe();
  }

  public onEmailTemplateChanged(): void {
    this.email.fromEmail = "";
    this.loadEmailTemplate();
  }

  /** fill inputs from email template */
  private loadEmailTemplate(): void {
    if (this.email.emailTemplateId) {
      const template = this.emailTemplates.find((template) => template.emailTemplateId === this.email.emailTemplateId)!;
      this.email.body = template.body;
      this.email.subject = template.subject;

      this.attachedFiles = [];
      this.email.attachments = [];
      template.attachments.forEach(att => {
        this.attachedFiles.push({
          id: att.emailTemplateAttachmentId,
          fileName: att.fileName,
          fileSize: att.fileSize,
          downloadUrl: att.downloadURL
        });
        this.email.attachments.push({
          fileId: att.emailTemplateAttachmentId,
          fileName: att.fileName,
          fileSize: att.fileSize,
          filePath: att.filePath,
          downloadURL: att.downloadURL
        });
      });

      this.fillDefaultFromEmail(template.sendEmailFromRecruiter);
    } else {
      this.fillDefaultFromEmail(false);
    }
  }

  /**
   * set the "from" value.
   * do not change the value if not empty
   * @param sendEmailFromRecruiter
   */
  private fillDefaultFromEmail(sendEmailFromRecruiter: boolean): void {
    if (this.email.fromEmail == "" || this.email.fromEmail == undefined) {
      this.email.fromEmail = this._defaultEmail;
      if (sendEmailFromRecruiter) {
        this.email.fromEmail = this._currentUserEmail;
      }
    }
  }

  private loadEmailLookups(): void {
    this.isLoading = true;
    this.isLoadingEmitter.emit(true);

    let filter: EmailFilterDTO = {
      filterEmailTemplates: this.filterEmailTemplates,
      allowedEmailTemplateIds: this.allowedEmailTemplateIds,
      showRecipient: this.showRecipient,
      showEmailCC: this.showEmailCC
    };

    this._getEmailEditorDataSubscription = this._emailsService.getEmailEditorData(filter)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.isLoadingEmitter.emit(false);
        this.toggleDisabled();
      }))
      .subscribe(r => {
        this.setInitialData(r);
      });
  }

  private setInitialData(r: EmailEditorInitDTO): void {
    //this.priorities = toLookupItems(r.priorities);
    this.emailTemplates = r.emailTemplates;
    this._defaultEmail = r.defaultAdminEmail;
    this._currentUserEmail = r.currentUserEmail;

    if (this.email.emailTemplateId) {
      this.onEmailTemplateChanged();
    }
    // auto select single template
    else if (this.noTemplate == false && this.emailTemplates.length === 1) {
      this.email.emailTemplateId = this.emailTemplates[0].emailTemplateId;
      this.onEmailTemplateChanged();
    }
    else {
      this.attachedFiles = [];
      this.email.attachments.forEach(att => {
        this.attachedFiles.push({
          id: att.fileId,
          fileName: att.fileName,
          fileSize: att.fileSize,
          downloadUrl: att.downloadURL
        });
      });
    }
  }
  private toggleDisabled(): void {
    this.isDisabledInternal = this.isLoading;
  }

  public validate(): boolean {
    this.formViewChild.form.markAllAsTouched();

    if (this.formViewChild.invalid) {
      return false;
    }
    return true;
  }

  public onModelChange(event: FileChangedEvent): void {
    if (event.isRemoved) {
      this.email.attachments = this.email.attachments?.filter(att => att.fileId !== event.id);
    }
    else {
      this.email.attachments?.push({
        fileId: event.id,
        fileName: event.fileName,
        fileSize: event.fileSize,
        filePath: event.filePath,
        downloadURL: ''
      })
    }
  }
}
